import { useRef, useState, useMemo, useCallback, type ChangeEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  Text,
  useDisclosure,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react'
import { Search, LayoutList, User, UserX, Trash2 } from 'lucide-react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAccessCheck } from '@/hooks/useAccess'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'
import ReassignModal from '@/components/helpdesk/ReassignModal'
import { useAuthStore } from '@/store/auth'
import type { MetaData } from '@/components/tables/types'

type TabValue = 'all' | 'my' | 'unassigned' | 'trash'

const basePath = '/ticket-management/all-tickets'

const TABS: Array<{ key: TabValue; label: string; icon: typeof LayoutList }> = [
  { key: 'all', label: 'All Tickets', icon: LayoutList },
  { key: 'my', label: 'Assigned Tickets', icon: User },
  { key: 'unassigned', label: 'Unassigned Tickets', icon: UserX },
  { key: 'trash', label: 'Trash', icon: Trash2 },
]

export default function AllTicketsPage() {
  const colors = useThemeColors()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalMy, setTotalMy] = useState(0)
  const [totalUnassigned, setTotalUnassigned] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')
  const [reassignTicketId, setReassignTicketId] = useState<number | null>(null)
  const [reassignCurrentAssignedToId, setReassignCurrentAssignedToId] = useState<number | null>(null)
  const { isOpen: isReassignOpen, onOpen: onReassignOpen, onClose: onReassignClose } = useDisclosure()
  const [searchParams] = useSearchParams()
  const urlStatus = searchParams.get('status') ?? undefined
  const urlSlaStatus = searchParams.get('sla_status') ?? undefined
  const urlAssigned = searchParams.get('assigned') ?? undefined

  const access = useAccessCheck()

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      showActionsColumn: true,
      dataFields: {
        request_number: { name: 'Request #', withSort: true, maxWidth: '160px' },
        contact_name: { name: 'Contact', withSort: true, maxWidth: '140px' },
        contact_email: { name: 'Email', withSort: true, maxWidth: '180px' },
        ticket_status_label: {
          name: 'Status',
          withSort: true,
          maxWidth: '120px',
          badge: {
            'New': 'blue',
            'Assigned': 'purple',
            'In Progress': 'teal',
            'Pending': 'yellow',
            'Cancelled': 'gray',
            'Resolved': 'green',
            'Closed': 'gray',
            'For Approval': 'orange',
            'Approved': 'green',
            'Rejected': 'red',
          },
        },
        service_type_name: { name: 'Service type', withSort: true, maxWidth: '140px' },
        assigned_to_name: { name: 'Assigned To', withSort: false, maxWidth: '160px' },
        for_approval: {
          name: 'For approval',
          withSort: true,
          maxWidth: '100px',
          badge: { 1: 'orange', 2: 'gray', 3: 'blue' },
          badgeLabels: { 1: 'Yes', 2: 'No', 3: 'Auto' },
        },
        created_at_human: { name: 'Created', withSort: true, maxWidth: '160px' },
      },
      dataSource: activeTab === 'trash' ? `${basePath}/archived` : basePath,
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: 'No tickets found',
      hideSelection: false,
      otherActions: [
        {
          name: 'Update status',
          onClick: (row: { id: number }) => navigate(`/ticket-management/all-tickets/${row.id}/update-status`),
        },
      ],
    }),
    [activeTab, navigate]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access]
  )

  const tableParams = useMemo(
    () => ({
      search: searchQuery || undefined,
      trash: activeTab === 'trash' ? 1 : 0,
      ...(activeTab === 'my' && user?.id && { assigned_to: user.id }),
      ...(activeTab === 'unassigned' && { unassigned: 1 }),
      ...(urlStatus && { status: urlStatus }),
      ...(urlSlaStatus && { sla_status: urlSlaStatus }),
      ...(urlAssigned && { assigned: urlAssigned }),
    }),
    [searchQuery, activeTab, user?.id, urlStatus, urlSlaStatus, urlAssigned]
  )

  const openReassign = useCallback(
    (id: number, assignedTo: number | null) => {
      setReassignTicketId(id)
      setReassignCurrentAssignedToId(assignedTo)
      onReassignOpen()
    },
    [onReassignOpen]
  )

  const customRenderers = useMemo(
    () => ({
      assigned_to_name: (_value: unknown, row: { id: number; assigned_to: number | null; assigned_to_name?: string }) => (
        <Button
          variant="link"
          size="sm"
          colorScheme={row.assigned_to == null ? 'orange' : 'blue'}
          onClick={(e) => {
            e.stopPropagation()
            openReassign(row.id, row.assigned_to ?? null)
          }}
        >
          {row.assigned_to == null ? 'Unassigned' : (row.assigned_to_name ?? '—')}
        </Button>
      ),
    }),
    [openReassign]
  )

  const setSubSub = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const onDataLoad = useMemo(
    () => (data: unknown[], meta?: MetaData) => {
      if (meta?.all != null) setTotalRows(meta.all)
      if (meta?.trashed != null) setTotalTrash(meta.trashed)
      if (meta?.my != null) setTotalMy(meta.my)
      if (meta?.unassigned != null) setTotalUnassigned(meta.unassigned)
    },
    []
  )

  const tabIndex = TABS.findIndex((t) => t.key === activeTab)
  const handleTabChange = (index: number) => {
    const tab = TABS[index]
    if (tab) {
      setActiveTab(tab.key)
      setSearchQuery('')
      tableRef.current?.applyFilter('search', '')
      tableRef.current?.clearPage()
    }
  }

  const handleSearch = () => {
    tableRef.current?.applyFilter('search', searchQuery || '')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }
    setModalMessage(
      activeTab === 'trash' && bulkAction === 'delete'
        ? 'You are about to permanently delete these tickets. This action cannot be undone.'
        : 'Are you sure you want to apply this action?'
    )
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    setIsBulkProcessing(true)
    try {
      if (bulkAction === 'restore') {
        for (const id of selectedIds) {
          await apiClient.put(`${basePath}/restore/${id}`)
        }
        showSuccess('Success', selectedIds.length > 1 ? `${selectedIds.length} tickets restored` : 'Ticket restored successfully')
      } else if (bulkAction === 'delete' && activeTab === 'trash') {
        for (const id of selectedIds) {
          await apiClient.delete(`${basePath}/force-delete/${id}`)
        }
        showSuccess('Success', selectedIds.length > 1 ? `${selectedIds.length} tickets permanently deleted` : 'Ticket permanently deleted')
      } else if (bulkAction === 'delete' && activeTab === 'all') {
        for (const id of selectedIds) {
          await apiClient.delete(`${basePath}/${id}`)
        }
        showSuccess('Success', selectedIds.length > 1 ? `${selectedIds.length} tickets moved to trash` : 'Ticket moved to trash')
      }
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
    } catch (error: unknown) {
      showError(error as Error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex={1} overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex={1} minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                All Tickets
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                View and manage all ticket requests
              </Text>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={tabIndex >= 0 ? tabIndex : 0} onChange={handleTabChange} variant="enclosed">
                    <TabList borderBottom="none">
                      {TABS.map(({ key, label, icon: Icon }) => (
                        <Tab key={key}>
                          <HStack spacing={2}>
                            <Icon size={16} />
                            <Text>{label}</Text>
                            {key === 'all' && <Badge ml={2} colorScheme="blue">{totalRows}</Badge>}
                            {key === 'my' && <Badge ml={2} colorScheme="teal">{totalMy}</Badge>}
                            {key === 'unassigned' && <Badge ml={2} colorScheme="orange">{totalUnassigned}</Badge>}
                            {key === 'trash' && <Badge ml={2} colorScheme="red">{totalTrash}</Badge>}
                          </HStack>
                        </Tab>
                      ))}
                    </TabList>
                  </Tabs>
                  <Box pb={2}>
                    <ActionButtons
                      pathname={basePath}
                      createRoute={`${basePath}/create`}
                      createLabel="Create Ticket"
                    />
                  </Box>
                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0} px={5}>
              <HStack spacing={4} flexWrap="wrap">
                {access.can_delete && (
                  <HStack spacing={2} minW="250px">
                    <Select
                      placeholder="Bulk actions"
                      size="sm"
                      value={bulkAction}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => setBulkAction(e.target.value)}
                    >
                      {activeTab === 'trash' && <option value="restore">Restore</option>}
                      {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                      {activeTab !== 'trash' && <option value="delete">Move to trash</option>}
                    </Select>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={handleBulkActionClick}
                      isLoading={isBulkProcessing}
                    >
                      Apply
                    </Button>
                  </HStack>
                )}
                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search by request #, contact, email..."
                      value={searchQuery}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                      onKeyDown={handleKeyDown}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody p={0} pt={1} pb={4} mx={5} flex={1} overflow="hidden" display="flex" flexDirection="column" minH={0}>
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  setSubSub={setSubSub}
                  onDataLoad={onDataLoad}
                  access={permissions}
                  customRenderers={customRenderers}
                />
              </Box>
            </CardBody>
          </Card>
          <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Confirm</ModalHeader>
              <ModalBody>{modalMessage}</ModalBody>
              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onClose}>
                  Cancel
                </Button>
                <Button colorScheme="blue" onClick={handleConfirmBulkAction} isLoading={isBulkProcessing}>
                  Confirm
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {reassignTicketId != null && (
            <ReassignModal
              isOpen={isReassignOpen}
              onClose={() => {
                onReassignClose()
                setReassignTicketId(null)
                setReassignCurrentAssignedToId(null)
              }}
              ticketId={reassignTicketId}
              currentAssignedToId={reassignCurrentAssignedToId}
              onSuccess={() => {
                tableRef.current?.reload()
              }}
            />
          )}

        </VStack>
      </Box>
    </AdminLayout>
  )
}
